/* @flow */
import Vue from 'vue'
import axios from 'axios'
import normalize from 'jsonapi-normalizer'
import oPath from 'object-path'
import query from 'query-string'

import {REMOVE_SESSION} from './session'
import {NEW_NOTIFICATION, REPLACE_NOTIFICATION} from './notifications'
import type {JsonApiEntity} from '@/store'

export const REPLACE_DEVICE_GROUPS = 'REPLACE_DEVICE_GROUPS'
export const ADD_DEVICE_GROUPS = 'ADD_DEVICE_GROUPS'
export const MODIFY_DEVICE_GROUP = 'MODIFY_DEVICE_GROUP'
export const REMOVE_DEVICE_GROUP = 'REMOVE_DEVICE_GROUP'
export const START_DEVICE_GROUP_FETCHING = 'START_DEVICE_GROUP_FETCHING'
export const DONE_DEVICE_GROUP_FETCHING = 'DONE_DEVICE_GROUP_FETCHING'
export const SET_DEVICE_GROUPS_TOTAL = 'SET_DEVICE_GROUPS_TOTAL'

export const FETCH_DEVICE_GROUPS = 'FETCH_DEVICE_GROUPS'
export const FETCH_DEVICE_GROUP = 'FETCH_DEVICE_GROUP'
export const CREATE_DEVICE_GROUP = 'CREATE_DEVICE_GROUP'
export const UPDATE_DEVICE_GROUP = 'UPDATE_DEVICE_GROUP'
export const ADD_DEVICES_TO_DEVICE_GROUP = 'ADD_DEVICES_TO_DEVICE_GROUP'
export const REMOVE_DEVICES_FROM_DEVICE_GROUP = 'REMOVE_DEVICES_FROM_DEVICE_GROUP'
export const DELETE_DEVICE_GROUP = 'DELETE_DEVICE_GROUP'
export const RESET_STATE = 'RESET_STATE'

const initialState = () => ({
  entities: {},
  statistics: {
    total: null,
  },
  fetch_status: {
    all: '',
  },
})

export default {
  state: initialState(),
  getters: {
    deviceGroupDefaults: () => ({}),
    deviceGroups: ({entities = {}}: Object) =>
      //$FlowFixMe
      Object.keys(entities).map(key => entities[key]),
    deviceGroupsKeyedByEui: (state: Object, getters: Object) => {
      const deviceGroups = {}
      getters.deviceGroups.forEach(group => {
        deviceGroups[group.eui] = group
      })
      return deviceGroups
    },
    deviceGroupById: (state: Object) => (id: string) => state.entities[id] || {},
    deviceGroupByEui: (state: Object, getters: Object) => (eui: string) =>
      getters.deviceGroups.find(group => group.eui === eui) || {},
  },
  mutations: {
    [RESET_STATE](state: Object) {
      Object.assign(state, initialState())
      state.entities = initialState().entities
    },
    [REMOVE_SESSION](state: Object) {
      state.entities = initialState().entities
    },
    [START_DEVICE_GROUP_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = 'loading'
    },
    [DONE_DEVICE_GROUP_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = 'loaded'
    },
    [REPLACE_DEVICE_GROUPS](state: Object, {entities = {}}: Object) {
      state.entities = entities
    },
    [ADD_DEVICE_GROUPS](state: Object, payload: Object) {
      state.entities = {
        ...state.entities,
        ...payload.entities,
      }
    },
    [MODIFY_DEVICE_GROUP](state: Object, payload: Object) {
      state.entities[payload.id] = {
        ...state.entities[payload.id],
        ...payload,
      }
    },
    [REMOVE_DEVICE_GROUP](state: Object, payload: Object) {
      Vue.delete(state.entities, payload.id)
    },
    [SET_DEVICE_GROUPS_TOTAL](state: Object, payload: Object) {
      state.statistics.total = payload.total
    },
  },
  actions: {
    [RESET_STATE]: async function(state: any) {
      this.commit(RESET_STATE, state)
    },
    [FETCH_DEVICE_GROUPS]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        commit(START_DEVICE_GROUP_FETCHING, {key: 'all'})
        let pathstring = '/device_groups'

        if (payload && payload.orgId) {
          pathstring = '/organizations/' + payload.orgId + '/device_groups'
        }

        pathstring += '?limit=1000'

        const {data} = await axios.get(pathstring)

        commit(SET_DEVICE_GROUPS_TOTAL, {
          total: data.meta.pagination.collection_count,
        })
        const entities = {}
        data.data.map(group => {
          entities[group.id] = {
            ...group.attributes,
            ...group.meta,
          }
        })

        commit(REPLACE_DEVICE_GROUPS, {
          entities,
        })
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load operation groups.',
          detail: err.message,
          color: 'error',
        })
      } finally {
        commit(DONE_DEVICE_GROUP_FETCHING, {key: 'all'})
      }
    },
    [FETCH_DEVICE_GROUP]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        const {
          data: {data},
        } = await axios.get(`/device_groups/${payload.id}`)
        commit(ADD_DEVICE_GROUPS, {
          entities: {
            [data.id]: {
              ...data.attributes,
              ...data.meta,
            },
          },
        })
        return {success: true}
      } catch (err) {
        const status = oPath.get(err, 'response.status')
        if (status !== 404) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Failed to load operation group.',
            detail: err.message,
            color: 'error',
          })
        }
        return {success: false, status}
      }
    },
    [CREATE_DEVICE_GROUP]: async function(
      {commit, dispatch}: Object,
      payload: JsonApiEntity
    ) {
      try {
        const {data} = await axios.post('/device_groups', {
          data: {
            type: 'device_group',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        if (!payload.suppressNotifications) {
          commit(ADD_DEVICE_GROUPS, {
            entities: entities.device_groups,
          })
        }
        dispatch(NEW_NOTIFICATION, {
          text: 'Saved new operation group!',
          color: 'success',
        })
        return data.data.attributes.eui
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to save operation group',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [UPDATE_DEVICE_GROUP]: async function({commit, dispatch}: Object, payload: Object) {
      let notificationId
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: 'Saving...',
          undoable: true,
          color: 'secondary',
        })
      }

      try {
        const {data} = await axios.patch(`/device_groups/${payload.id}`, {
          data: {
            type: 'device_groups',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(MODIFY_DEVICE_GROUP, entities.device_groups[payload.id])
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: 'Saved!',
            color: 'success',
            undoable: true,
          })
        }
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to update operation group.',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [REMOVE_DEVICES_FROM_DEVICE_GROUP]: async function(
      {getters, dispatch}: Object,
      payload: Object
    ) {
      const euis = [...getters.deviceGroupById(payload.id).device_euis]
      payload.device_euis.map(eui => {
        euis.splice(euis.indexOf(eui), 1)
      })

      await dispatch(UPDATE_DEVICE_GROUP, {
        id: payload.id,
        attributes: {
          device_euis: euis,
        },
      })
    },
    [ADD_DEVICES_TO_DEVICE_GROUP]: async function(
      {getters, dispatch}: Object,
      payload: Object
    ) {
      await dispatch(UPDATE_DEVICE_GROUP, {
        id: payload.id,
        attributes: {
          device_euis: [
            ...getters.deviceGroupById(payload.id).device_euis,
            ...payload.device_euis,
          ],
        },
      })
    },
    [DELETE_DEVICE_GROUP]: async function({commit, dispatch}: Object, payload: Object) {
      let notificationId
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: `Deleting operation group...`,
          color: 'secondary',
        })
      }

      try {
        await axios.delete(
          `/device_groups/${payload.id}?${query.stringify(payload.options)}`
        )
        commit(REMOVE_DEVICE_GROUP, payload)
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: `Deleted operation group!`,
            color: 'success',
          })
        }
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to delete operation group.',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/device_groups.js